import { useCallback, useEffect, useMemo, useState } from 'react';
import type { RequestGroup, TrackedRequest } from '../types';
import { ApiError, api } from '../api/client';
import { Button, Card, Input, Notice, Spinner } from '../components/ui/primitives';
import { CombinationTabs } from '../components/requests/CombinationTabs';
import {
  Pill,
  agencyLabel,
  formatDate,
  pushProgress,
} from '../components/requests/shared';

/**
 * Screen 0 — Requests.
 *
 * One row per **brand and agency**, which is also one row per rollout document.
 * A document gains a tab per push request, so a brand pushed to eleven times used
 * to fill eleven near-identical rows and the operator had to read all of them to
 * find the one they wanted. The tabs now live on a second screen, which is also
 * where an audience push is recorded — a push belongs to a tab, not to a brand.
 *
 * Grouping is done by the server so the rule sits in one place and under test.
 */
export function RequestsScreen({
  openKey,
  onOpenKeyChange,
  onNewRequest,
  onOpenForm,
}: {
  /**
   * Which combination is open, held by the flow controller rather than here.
   *
   * Opening a request in the form unmounts this screen; keeping the selection
   * locally would lose it, and Back would land on the top-level list instead of
   * the tabs the operator came from.
   */
  openKey: string | null;
  onOpenKeyChange: (key: string | null) => void;
  onNewRequest: () => void;
  onOpenForm: (entry: TrackedRequest, mode: 'view' | 'amend' | 'duplicate') => void;
}) {
  const [requests, setRequests] = useState<TrackedRequest[] | null>(null);
  const [groups, setGroups] = useState<RequestGroup[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const response = await api.listRequests();
      setRequests(response.requests);
      setGroups(response.groups);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The requests could not be loaded.');
      setRequests([]);
      setGroups([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Applies a saved push without a round trip.
   *
   * The group totals are recomputed from its tabs rather than adjusted by a
   * delta, so a correction that lowers a count cannot leave the header claiming
   * more audiences are live than actually are.
   */
  const applyUpdate = useCallback((updated: TrackedRequest) => {
    setRequests((current) =>
      current ? current.map((entry) => (entry.id === updated.id ? updated : entry)) : current,
    );

    setGroups((current) =>
      current.map((group) => {
        if (!group.requests.some((entry) => entry.id === updated.id)) return group;

        const requestsInGroup = group.requests.map((entry) =>
          entry.id === updated.id ? updated : entry,
        );

        return {
          ...group,
          requests: requestsInGroup,
          cohortCount: requestsInGroup.reduce((total, entry) => total + entry.cohortCount, 0),
          pushedCount: requestsInGroup.reduce((total, entry) => total + entry.pushedCount, 0),
          pushCount: requestsInGroup.reduce((total, entry) => total + entry.pushCount, 0),
        };
      }),
    );
  }, []);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return groups;

    /*
     * Matched against the group *and* its tabs, so searching a channel or a tab
     * title still finds the combination that holds it.
     */
    return groups.filter((group) =>
      [
        group.brandName,
        group.agencyName,
        ...group.subAgencyNames,
        ...group.channels,
        group.documentName,
        ...group.requests.map((entry) => `${entry.tabTitle} ${entry.submittedBy}`),
      ]
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
  }, [groups, query]);

  const open = groups.find((group) => group.key === openKey) ?? null;

  if (open) {
    return (
      <CombinationTabs
        group={open}
        onBack={() => onOpenKeyChange(null)}
        onRequestUpdated={applyUpdate}
        onOpenForm={onOpenForm}
      />
    );
  }

  return (
    <div className="space-y-5">
      <Card className="p-4 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-section font-semibold text-ink">Audience Push Requests</p>
            <p className="mt-0.5 text-micro text-ink-muted">
              {requests === null
                ? 'Loading…'
                : `${groups.length} brand and agency combination${groups.length === 1 ? '' : 's'}` +
                  ` · ${requests.length} tab${requests.length === 1 ? '' : 's'}`}
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Input
              value={query}
              placeholder="Search brand, agency, channel…"
              onChange={(event) => setQuery(event.target.value)}
              className="sm:w-72"
            />
            <Button onClick={onNewRequest} className="shrink-0 justify-center">
              New request
            </Button>
          </div>
        </div>
      </Card>

      {error ? (
        <Notice
          tone="error"
          title="Could not load requests"
          action={
            <Button variant="secondary" onClick={() => void load()} className="px-4 py-1.5">
              Try again
            </Button>
          }
        >
          {error}
        </Notice>
      ) : null}

      {requests === null ? (
        <Card className="p-8">
          <div className="flex items-center justify-center gap-2.5 text-field text-ink-muted">
            <Spinner />
            Loading requests…
          </div>
        </Card>
      ) : !visible.length ? (
        <Card className="p-8">
          <div className="text-center">
            <p className="text-field font-medium text-ink">
              {groups.length ? 'Nothing matches that search.' : 'No requests yet.'}
            </p>
            <p className="mt-1 text-micro text-ink-muted">
              {groups.length
                ? 'Clear the search to see them all.'
                : 'Create the first one and it will be tracked here.'}
            </p>
            {!groups.length ? (
              <div className="mt-4">
                <Button onClick={onNewRequest}>New request</Button>
              </div>
            ) : null}
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          {/*
            Fixed column widths with truncation, so a long document name cannot
            squeeze the action off the right edge — which is what happened when
            the browser was left to size them.
          */}
        {/*
          A six-column table needs about 62rem to be readable, so on a phone it
          became a sideways scroll through columns the reader cannot see at once.
          Below `md` each row becomes a card instead — the same fields, stacked in
          reading order, with the whole card tappable.
        */}
        <ul className="divide-y divide-line md:hidden">
          {visible.map((group) => {
            const progress = pushProgress(group.pushedCount, group.cohortCount);

            return (
              <li key={group.key}>
                <button
                  type="button"
                  onClick={() => onOpenKeyChange(group.key)}
                  className="w-full px-4 py-4 text-left transition-colors active:bg-surface-muted"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-field font-medium text-ink">
                        {group.brandName || '—'}
                      </p>
                      <p className="mt-0.5 truncate text-micro text-ink-muted">
                        {agencyLabel({
                          businessType: group.businessType,
                          agencyName: group.agencyName,
                          subAgencyName: '',
                        })}
                        {group.subAgencyNames.length ? ` · ${group.subAgencyNames.join(', ')}` : ''}
                      </p>
                    </div>
                    <span aria-hidden="true" className="shrink-0 text-ink-faint">
                      ›
                    </span>
                  </div>

                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                    <Pill tone={progress.tone}>{progress.text}</Pill>
                    <span className="text-micro text-ink-muted">
                      {group.tabCount} tab{group.tabCount === 1 ? '' : 's'}
                    </span>
                    {group.channels.length ? (
                      <span className="text-micro text-ink-faint">
                        {group.channels.join(', ')}
                      </span>
                    ) : null}
                  </div>

                  <p className="mt-1.5 text-micro text-ink-faint">
                    Latest {formatDate(group.latestAt)}
                  </p>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[62rem] table-fixed border-collapse text-left">
              <colgroup>
                <col className="w-[24%]" />
                <col className="w-[22%]" />
                <col className="w-[13%]" />
                <col className="w-[10%]" />
                <col className="w-[16%]" />
                <col className="w-[15%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-line bg-surface-muted">
                  {['Brand', 'Agency', 'Channels', 'Tabs', 'Audience push', ''].map(
                    (heading, index) => (
                      <th
                        key={heading || `actions-${index}`}
                        scope="col"
                        className="px-4 py-3 text-micro font-semibold uppercase tracking-wide text-ink-muted"
                      >
                        {heading}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {visible.map((group) => {
                  const progress = pushProgress(group.pushedCount, group.cohortCount);

                  return (
                    <tr
                      key={group.key}
                      className="cursor-pointer border-b border-line align-top last:border-0 hover:bg-surface-muted/50"
                      onClick={() => onOpenKeyChange(group.key)}
                    >
                      <td className="px-4 py-4">
                        <span className="block truncate text-field font-medium text-ink">
                          {group.brandName || '—'}
                        </span>
                        <span
                          className="mt-0.5 block truncate text-micro text-ink-faint"
                          title={group.documentName}
                        >
                          {group.documentName}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="block truncate text-field text-ink-muted">
                          {agencyLabel({
                            businessType: group.businessType,
                            agencyName: group.agencyName,
                            subAgencyName: '',
                          })}
                        </span>
                        {group.subAgencyNames.length ? (
                          <span
                            className="mt-0.5 block truncate text-micro text-ink-faint"
                            title={group.subAgencyNames.join(', ')}
                          >
                            {group.subAgencyNames.join(', ')}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-4">
                        <span className="block truncate text-field text-ink-muted">
                          {group.channels.join(', ') || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-field text-ink-muted">{group.tabCount}</td>
                      <td className="px-4 py-4">
                        <Pill tone={progress.tone}>{progress.text}</Pill>
                        <span className="mt-1 block text-micro text-ink-faint">
                          Latest {formatDate(group.latestAt)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex justify-end">
                          <Button
                            variant="secondary"
                            className="px-3 py-1.5"
                            onClick={(event) => {
                              event.stopPropagation();
                              onOpenKeyChange(group.key);
                            }}
                          >
                            Manage tabs
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
